import { Component, Input, OnInit } from "@angular/core";
import * as Highcharts from "highcharts";
import { find, flatMap, map } from "lodash";
import { Series } from "../../store/metrics/state";

@Component({
  selector: "stacked-column-expanded",
  templateUrl: "./stacked-column-expanded.component.html",
  styleUrls: ["./stacked-column-expanded.component.scss"],
})
export class StackedColumnExpandedComponent implements OnInit {
  options: Object;
  chart;
  toolTipText: string;
  toolTipTitle: string;
  Highcharts = Highcharts;
  @Input()
  chartData;
  @Input()
  dataSuffix;
  @Input()
  dataPrefix;
  @Input()
  roundTo = 0;
  @Input()
  filterByText;
  @Input()
  contentText;
  @Input()
  compareByCategory;
  @Input()
  noComparisonText;
  @Input()
  schools;
  @Input()
  showLabels: boolean;
  @Input()
  selectedTimePeriods: Array<string>;

  legend: Array<string>;

  ngOnInit() {
    this.legend = [];
    //Remove duplicates from legend
    this.chartData = map(this.chartData, (series: Series) => {
      return { ...series, showInLegend: this.dedupeLegend(series.name) };
    });
    this.createChart();
  }

  ngOnChanges(change) {
    // redraw when label show/hide changes or time periods change
    if (change.showLabels || change.selectedTimePeriods) {
      this.createChart();
    }
  }

  dedupeLegend(name) {
    if (this.legend.indexOf(name) > -1) {
      return false;
    } else {
      this.legend.push(name);
      return true;
    }
  }

  createChart() {
    var self = this;
    this.options = {
      chart: {
        type: "column",
      },
      boost: {
        enabled: false,
      },
      xAxis: {
        type: "category",
        categories: self.selectedTimePeriods,
        labels: {
          y: 60,
          style: {
            fontSize: "0.8rem",
          },
        },
      },
      yAxis: {
        title: false,
        allowDecimals: false,
        min: 0,
        stackLabels: {
          enabled: true,
          verticalAlign: "bottom",
          crop: false,
          overflow: "none",
          y: 30,
          rotation: -50,
          formatter: function () {
            return self.compareByCategory == "School"
              ? self.mapSchoolNameById(this.stack)
              : this.stack;
          },
          style: {
            fontSize: "0.8rem",
            fontWeight: "lighter",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            width: "50rem",
          },
        },
      },
      plotOptions: {
        column: {
          stacking: "percent",
          dataLabels: {
            enabled: this.showLabels,
            useHTML: true,
            formatter: function () {
              let l = `
                <div style="text-align:center">
                  <div>
                    ${this.y}${self.dataSuffix}
                  </div>
                  <div style="font-weight:500">
                    (${this.point.numerator})
                  </div>
                </div>
              `;
              return this.y != null ? l : "";
            },
          },
        },
        series: {
          dataPrefix: this.dataPrefix,
          dataSuffix: this.dataSuffix,
          events: {
            legendItemClick: function () {
              return false;
            },
          },
        },
      },
      title: { text: null },
      series: this.chartData,
      credits: false,
      legend: {
        symbolHeight: 12,
        symbolWidth: 12,
        symbolRadius: 6,
        layout: "vertical",
        align: "right",
        verticalAlign: "middle",
        itemMarginBottom: 2,
        itemStyle: {
          fontWeight: 400,
          fontSize: "1.1rem",
          fontFamily: "'Lato', Helvetica, sans-serif",
          color: "#6E6E6E",
          cursor: "default",
        },
        itemHoverStyle: {
          color: "#6e6e6e",
        },
        labelFormatter: function () {
          return this.name;
        },
      },
      responsive: {
        rules: [
          {
            condition: {
              maxWidth: 700,
            },
            chartOptions: {
              legend: {
                layout: "horizontal",
                align: "center",
                verticalAlign: "bottom",
              },
            },
          },
        ],
      },
      tooltip: {
        shared: false,
        useHTML: true,
        outside: false, //setting outside to false here because it was making the tooltip show up behind the MZ modal
        borderColor: "#aaa",
        backgroundColor: "rgba(255, 255, 255, 1.0)",
        shadow: false,
        headerFormat: "",
        style: {
          fontFamily: "'Lato', Helvetica, sans-serif",
          backgroundColor: "#FFFFFF",
        },
        formatter: function () {
          let tooltipDescriptor;
          if (this.series.userOptions.description) {
            tooltipDescriptor = `students ${this.series.userOptions.description}`;
          } else if (this.series.name) {
            tooltipDescriptor = `students ${this.series.name}`;
          } else {
            tooltipDescriptor = "students";
          }

          if (self.dataSuffix === "") {
            self.toolTipText = `${self.dataPrefix || ""}<strong>${
              this.point.numerator != null
                ? this.point.numerator.toLocaleString(undefined, {
                    maximumFractionDigits: self.roundTo,
                  })
                : ""
            }</strong> ${tooltipDescriptor}`;
          } else {
            self.toolTipText = `${self.dataPrefix || ""}<strong>${
              this.point.numerator != null
                ? this.point.numerator.toLocaleString(undefined, {
                    maximumFractionDigits: self.roundTo,
                  })
                : ""
            }</strong> of ${self.dataPrefix || ""}<strong>${
              this.point.denominator != null
                ? this.point.denominator.toLocaleString(undefined, {
                    maximumFractionDigits: self.roundTo,
                  })
                : ""
            }</strong> ${tooltipDescriptor}`;
          }
          self.toolTipTitle = `${this.point.name} ${this.series.name}`;

          let titleText =
            `<span class="metric-tooltip title capitalize tile_body">` +
            (self.compareByCategory &&
            self.compareByCategory != self.noComparisonText
              ? `<div>
                 ${self.compareByCategory}:
                 <span class="body_tooltip_text">
                  <strong>${
                    self.compareByCategory == "School"
                      ? self.mapSchoolNameById(this.series.userOptions.stack)
                      : this.series.userOptions.stack
                  }</strong>
                 </span>
               </div>`
              : ``) +
            (self.filterByText
              ? `<div>
                 Filtered by:
                 <span class="body_tooltip_text"><strong>${self.filterByText}</strong></span>
               </div>`
              : ``) +
            (this.point.name
              ? `<div>
                 Time Period:
                 <span class="body_tooltip_text"><strong>${this.point.name}</strong></span>
               </div>`
              : ``) +
            (this.series.name
              ? `<div>
                 Performance Level:
                 <span class="body_tooltip_text"><strong>${this.series.name}</strong></span>
               </div>`
              : ``) +
            `</span>`;

          return (
            `<div class="line-expanded-titles">
                      ${titleText}
                      <span class="metric-tooltip value body_tooltip_text"><strong>` +
            (this.point.alphaValue
              ? this.point.alphaValue
              : `${self.dataPrefix || ""}${
                  this.point.y != null
                    ? this.point.y.toLocaleString(undefined, {
                        minimumFractionDigits: self.roundTo,
                        maximumFractionDigits: self.roundTo,
                      })
                    : ""
                }${self.dataSuffix}`) +
            `</strong></span>
                    </div>
                    <div class="metric-tooltip description body_tooltip_text">${self.toolTipText}</div>`
          );
        },
      },
    };
  }

  updateChartWithNewData(newData) {
    // dedupe legend
    this.legend = [];
    newData = {
      ...newData,
      series: map(newData.series, (s: Series) => {
        return { ...s, showInLegend: this.dedupeLegend(s.name) };
      }),
    };
    /* OneToOne: 
    When true, the series, xAxis, yAxis and annotations collections will be updated one to one, 
    and items will be either added or removed to match the new updated options. 
    For example, if the chart has two series and we call chart.update with a configuration containing three series, one will be added. 
    If we call chart.update with one series, one will be removed. */
    this.chart.update(newData, true, true); // sets redraw and oneToOne both to be true
  }

  mapSchoolNameById(systemSchoolId) {
    let school = find(this.schools, ["systemSchoolId", systemSchoolId]);
    return school ? school.officialSchoolName : "";
  }

  chartCallback = (chart) => {
    this.chart = chart;
    setTimeout(() => {
      if (chart.chartHeight !== chart.renderTo.clientHeight) {
        chart.setSize(undefined, null, true);
      }
    });
  };
}
